/**
 * Publishes `--sticky-header-offset` on the body: how far down the viewport the header group
 * reaches right now, counting only the parts of it that are pinned there. Anything that sticks
 * below the header uses it as its `top`.
 *
 * It measures rather than reading the header's settings because what is pinned changes with the
 * header's sticky mode and scroll direction, and can include the announcement bar as well as the
 * header: a header that only sticks on scroll-up is pinned one moment and gone the next, and the
 * announcement bar sticks or scrolls away depending on how its section is set up. Whatever is
 * pinned is counted; whatever has scrolled off is not; with nothing pinned, the offset is 0.
 *
 * A sticky section counts where it pins, not where it is. It sits in the flow above everything
 * that uses the offset, so nothing below it can stick until it has pinned. Its pinned bottom is
 * the one value those elements ever stick against, and it doesn't change as the page scrolls.
 * Measuring where the section is instead lags a fast scroll by a frame or more, which left a
 * gap the width of the announcement bar between the header and whatever stuck under it.
 */

const PROPERTY = '--sticky-header-offset';

/** @type {number | null} */
let frame = null;

/**
 * How far down the viewport a pinned section reaches. A sticky section is measured where it pins:
 * its `top` plus its height. A transparent header is absolutely positioned over the page, so its
 * section box is 0px tall and the header's own box is what counts; any absolutely positioned child
 * is measured alongside the section for that reason. Those are measured where they are, since the
 * page runs on underneath them and can stick there before they pin.
 *
 * @param {Element} section
 * @param {CSSStyleDeclaration} style - The section's computed style
 * @returns {number}
 */
function pinnedBottom(section, style) {
  let bottom = 0;

  const rect = section.getBoundingClientRect();
  if (rect.height > 0) {
    bottom = style.position === 'sticky' ? (parseFloat(style.top) || 0) + rect.height : rect.bottom;
  }

  for (const child of section.children) {
    if (getComputedStyle(child).position !== 'absolute') continue;

    const rect = child.getBoundingClientRect();
    if (rect.height === 0 || rect.bottom <= 0) continue;

    bottom = Math.max(bottom, rect.bottom);
  }

  return bottom;
}

function measure() {
  frame = null;

  let offset = 0;
  const group = document.getElementById('header-group');

  // The group is `display: contents` while the header is sticky, so its sections are measured
  // one by one rather than as a box.
  for (const section of group?.children ?? []) {
    const style = getComputedStyle(section);
    if (style.position !== 'sticky' && style.position !== 'fixed') continue;
    if (style.visibility === 'hidden') continue;

    offset = Math.max(offset, pinnedBottom(section, style));
  }

  // Rounded down, so a header of fractional height overlaps what sticks under it rather than
  // leaving a hairline of the page showing between them.
  document.body.style.setProperty(PROPERTY, `${Math.floor(offset)}px`);
}

function schedule() {
  if (frame === null) frame = requestAnimationFrame(measure);
}

// A module runs once per page however many sections load it, so this sets up a single listener.
function init() {
  addEventListener('scroll', schedule, { passive: true });
  addEventListener('resize', schedule, { passive: true });

  // The header flips between pinned and not by attribute, sometimes a frame after the scroll that
  // caused it, and its sections change height as they wrap or collapse.
  const group = document.getElementById('header-group');
  if (group) {
    new MutationObserver(schedule).observe(group, {
      subtree: true,
      attributes: true,
      attributeFilter: ['data-sticky-state', 'sticky', 'class', 'style', 'hidden'],
    });

    // Children too: a transparent header changes height without its 0px section noticing.
    const resize = new ResizeObserver(schedule);
    for (const section of group.children) {
      resize.observe(section);
      for (const child of section.children) resize.observe(child);
    }
  }

  measure();
}

init();
